/* ==========================================================================
   Prescription Details Component
   Task F16: Read-only summary with edit mode for prescription modifications
   ========================================================================== */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select, type SelectOption } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Pencil, RotateCcw, X, Check } from 'lucide-react'
import { usePortal } from '@/context/PortalContext'
import { CustomFieldsEdit } from '@/components/forms/CustomFieldsEdit'
import { drugCodeLabel } from '@/utils/countries'
import type { CatalogProductForPortal, ProductNoteDefinition } from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface PrescriptionDetailsFormData {
  dose_specific_directions: string
  quantity: string
  unit_amount: string
  days_supply: number
  refills: number
}

export interface PrescriptionDetailsProps {
  /** The selected catalog product */
  product: CatalogProductForPortal | null
  /** Disable all form fields */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

interface ModificationState {
  dose_specific_directions: boolean
  quantity: boolean
  days_supply: boolean
  refills: boolean
}

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const REFILL_OPTIONS: SelectOption[] = Array.from({ length: 12 }, (_, i) => ({
  value: String(i),
  label: String(i),
}))

// Max count a prescriber can enter for compound units (e.g., 200 × "60g")
const MAX_PACK_QUANTITY = 200

/* --------------------------------------------------------------------------
   Quantity Unit Parsing
   -------------------------------------------------------------------------- */

/**
 * Parse unit_amount to detect compound units (e.g., "15ml vial" → multiplier=15).
 * Returns null multiplier for simple units (e.g., "mL", "g", "cap").
 */
function parseUnitAmount(unit: string): { multiplier: number; baseUnit: string } | null {
  const match = unit.trim().match(/^(\d+(?:\.\d+)?)\s*(.+)$/)
  if (!match) return null
  return { multiplier: parseFloat(match[1]), baseUnit: match[2].trim() }
}

/**
 * Label for a compound-unit quantity, shown as "count (total)".
 * Value stored is the count.
 * e.g., for "60g": "1 (60)", "2 (120)", "3 (180)"
 * Returns null for simple units or when quantity isn't a number yet.
 */
function getQuantityTotalLabel(unit: string, quantity: string): string | null {
  const parsed = parseUnitAmount(unit)
  if (!parsed || !quantity) return null
  const count = Number(quantity)
  if (!Number.isFinite(count)) return null
  return `${count} (${parsed.multiplier * count})`
}

/**
 * Render quantity for read-only display as JSX.
 * - Compound units: quantity is the count, shown as count*unit with unit greyed out
 * - Simple units (e.g., "mL"): "7 mL"
 * - Bare number unit (e.g., "1"): just the quantity
 */
export function QuantityDisplay({ quantity, unitAmount }: { quantity: string; unitAmount: string }) {
  if (!quantity) return null
  // Strip trailing zeros: "2.00" → "2", "2.50" → "2.5", "0.5" → "0.5"
  const qty = quantity.includes('.') ? quantity.replace(/\.?0+$/, '') : quantity
  const parsed = parseUnitAmount(unitAmount)
  if (parsed) {
    return (
      <>
        {qty}
        <span className="text-[var(--color-gray-400)]">*{unitAmount}</span>
      </>
    )
  }
  // Simple unit — check if it's just a bare number
  if (/^\d+$/.test(unitAmount.trim())) return <>{qty}</>
  return <>{qty} {unitAmount}</>
}

/* --------------------------------------------------------------------------
   Helper Components
   -------------------------------------------------------------------------- */

/**
 * Badge to indicate a field has been modified from catalog defaults.
 */
function ModifiedBadge() {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--color-warning-light)] text-[var(--color-warning)]">
      Modified
    </span>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

/**
 * Prescription details display and edit component.
 * Shows read-only summary by default, with edit mode for modifications.
 * Must be used within a react-hook-form FormProvider.
 *
 * @example
 * ```tsx
 * const methods = useForm<PrescriptionFormData>()
 *
 * <FormProvider {...methods}>
 *   <PrescriptionDetails product={selectedProduct} />
 * </FormProvider>
 * ```
 */
export function PrescriptionDetails({
  product,
  disabled = false,
  className = '',
}: PrescriptionDetailsProps) {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<PrescriptionDetailsFormData>()

  const { config } = usePortal()
  // PORTAL-80 (Richie's): when directions mode is on, the prescriber picks a
  // predefined direction in the selector and the quantity/days-supply/refills
  // are locked — no free-form editing of these fields.
  const directionsLocked = !!config?.directions_dropdown_enabled

  const [isEditing, setIsEditing] = useState(false)
  const [originalValues, setOriginalValues] =
    useState<PrescriptionDetailsFormData | null>(null)

  // Watch form values for modification detection
  const currentDoseSpecificDirections = watch('dose_specific_directions')
  const currentQuantity = watch('quantity')
  const currentUnitAmount = watch('unit_amount')
  const currentDaysSupply = watch('days_supply')
  const currentRefills = watch('refills')

  // Smart quantity: detect compound units
  const isPackUnit = product ? parseUnitAmount(product.unit_amount) !== null : false

  // Check if any catalog defaults are missing (prescriber must fill them in)
  const hasMissingDefaults = product != null && (
    !product.dose_specific_directions ||
    !product.quantity ||
    product.days_supply == null ||
    product.refills == null
  )

  // Detect modifications compared to catalog defaults
  // Only flag as modified when the catalog had a default and prescriber changed it
  const modifications: ModificationState = {
    dose_specific_directions: product?.dose_specific_directions
      ? currentDoseSpecificDirections !== product.dose_specific_directions
      : false,
    quantity: product?.quantity
      ? currentQuantity !== product.quantity || currentUnitAmount !== product.unit_amount
      : false,
    days_supply: product?.days_supply != null
      ? currentDaysSupply !== product.days_supply
      : false,
    refills: product?.refills != null
      ? currentRefills !== product.refills
      : false,
  }

  const hasAnyModification = Object.values(modifications).some(Boolean)

  // Populate form when the SELECTED PRODUCT changes (handle null defaults).
  // Keyed on product.id, not the object reference: re-running when the same product
  // arrives as a new object (e.g. a catalog refetch / identity churn) would clobber a
  // restored draft's saved values, or the prescriber's in-flight edits, with the
  // catalog defaults. Resets correctly when the prescriber actually picks a new product.
  const lastInitializedProductId = useRef<string | null>(null)
  useEffect(() => {
    if (!product) return
    if (lastInitializedProductId.current === product.id) return
    lastInitializedProductId.current = product.id
    setValue('dose_specific_directions', product.dose_specific_directions || '')
    setValue('quantity', product.quantity || '')
    setValue('unit_amount', product.unit_amount)
    setValue('days_supply', product.days_supply ?? ('' as unknown as number))
    setValue('refills', product.refills ?? ('' as unknown as number))
  }, [product, setValue])

  // Auto-enter edit mode when defaults are missing — except in directions mode,
  // where the values come from the chosen direction and stay locked. Portal
  // config loads async, so directionsLocked can flip false→true after the user
  // has already entered edit mode; force edit mode off when it does so the
  // quantity/days-supply/refills can't be edited once locking applies.
  useEffect(() => {
    if (directionsLocked) {
      setIsEditing(false)
      // Directions mode locks these fields to the chosen product's catalog
      // values. Config loads async, so revert any edits made before locking
      // kicked in — otherwise stale modified values could be submitted.
      if (product) {
        setValue('dose_specific_directions', product.dose_specific_directions || '')
        setValue('quantity', product.quantity || '')
        setValue('unit_amount', product.unit_amount)
        setValue('days_supply', product.days_supply ?? ('' as unknown as number))
        setValue('refills', product.refills ?? ('' as unknown as number))
      }
      return
    }
    if (hasMissingDefaults) {
      setIsEditing(true)
    }
  }, [hasMissingDefaults, directionsLocked, product, setValue])

  // Reset to catalog defaults (only resets fields that have defaults)
  const handleReset = useCallback(() => {
    if (product) {
      if (product.dose_specific_directions) setValue('dose_specific_directions', product.dose_specific_directions)
      if (product.quantity) setValue('quantity', product.quantity)
      setValue('unit_amount', product.unit_amount)
      if (product.days_supply != null) setValue('days_supply', product.days_supply)
      if (product.refills != null) setValue('refills', product.refills)
    }
  }, [product, setValue])

  // Enter edit mode
  const handleEdit = useCallback(() => {
    setOriginalValues({
      dose_specific_directions: currentDoseSpecificDirections,
      quantity: currentQuantity,
      unit_amount: currentUnitAmount,
      days_supply: currentDaysSupply,
      refills: currentRefills,
    })
    setIsEditing(true)
  }, [
    currentDoseSpecificDirections,
    currentQuantity,
    currentUnitAmount,
    currentDaysSupply,
    currentRefills,
  ])

  // Cancel edit mode - restore original values
  const handleCancel = useCallback(() => {
    if (originalValues) {
      setValue('dose_specific_directions', originalValues.dose_specific_directions)
      setValue('quantity', originalValues.quantity)
      setValue('unit_amount', originalValues.unit_amount)
      setValue('days_supply', originalValues.days_supply)
      setValue('refills', originalValues.refills)
    }
    setIsEditing(false)
  }, [originalValues, setValue])

  // Save changes and exit edit mode (only if required fields are filled)
  const handleSave = useCallback(async () => {
    const valid = await trigger([
      'dose_specific_directions',
      'quantity',
      'unit_amount',
      'days_supply',
      'refills',
    ])
    if (!valid) return
    setIsEditing(false)
    setOriginalValues(null)
  }, [trigger])

  // Don't render if no product selected
  if (!product) {
    return null
  }

  // Read-only summary view
  if (!isEditing) {
    return (
      <Card className={`${className}`}>
        <div className="p-5">
          {/* Header with edit button */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[var(--color-charcoal)]">
              Prescription Details
            </h3>
            {/* Edit/Reset hidden in directions mode (PORTAL-80): the
                quantity/days-supply/refills are locked to the chosen direction. */}
            {!directionsLocked && (
              <div className="flex items-center gap-2">
                {hasAnyModification && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    disabled={disabled}
                    className="text-[var(--color-gray-600)]"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" aria-hidden="true" />
                    Reset to defaults
                  </Button>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleEdit}
                  disabled={disabled}
                >
                  <Pencil className="h-4 w-4 mr-1" aria-hidden="true" />
                  Edit
                </Button>
              </div>
            )}
          </div>

          {/* Summary content */}
          <div className="space-y-4">
            {/* Product info - always read-only */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Product
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.display_name}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Product Code
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.product_code}
                </dd>
              </div>
            </div>

            {/* NDC if present */}
            {product.ndc && (
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  {drugCodeLabel(config?.country)}
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.ndc}
                </dd>
              </div>
            )}

            {/* Ingredients if present */}
            {product.ingredients && (
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Ingredients
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.ingredients}
                </dd>
              </div>
            )}

            {/* Strength (read-only from catalog) */}
            {product.strength && (
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Strength
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.strength}
                </dd>
              </div>
            )}

            {/* Directions (editable) */}
            <div>
              <dt className="text-sm font-medium text-[var(--color-gray-500)] flex items-center gap-2">
                Directions
                {modifications.dose_specific_directions && <ModifiedBadge />}
              </dt>
              <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                {currentDoseSpecificDirections}
              </dd>
            </div>

            {/* Default Additional Directions (read-only from catalog) */}
            {product.default_additional_directions && (
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Additional Directions
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.default_additional_directions}
                </dd>
              </div>
            )}

            {/* Medical Necessity (read-only, conditional) */}
            {product.medical_necessity_statement && (
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Medical Necessity
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.medical_necessity_statement}
                </dd>
              </div>
            )}

            {/* Quantity and supply info */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] flex items-center gap-2">
                  Quantity
                  {modifications.quantity && <ModifiedBadge />}
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  <QuantityDisplay quantity={currentQuantity} unitAmount={currentUnitAmount} />
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] flex items-center gap-2">
                  Days Supply
                  {modifications.days_supply && <ModifiedBadge />}
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {currentDaysSupply}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] flex items-center gap-2">
                  Refills
                  {modifications.refills && <ModifiedBadge />}
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {currentRefills}
                </dd>
              </div>
            </div>

            {/* Product notes read-only display */}
            {product.product_notes && product.product_notes.length > 0 && (
              <ProductNotesReadOnly notes={product.product_notes} />
            )}

            {/* Custom fields (PORTAL-147): always editable, never a read-only
                summary. These values are prescriber-entered — the catalog has no
                default to summarize — and only the inputs carry the `required`
                rules, so keeping them mounted is what lets trigger() block
                submit. It also keeps them fillable in directions mode, where the
                Edit button is hidden entirely (PORTAL-80). */}
            {product.custom_fields && product.custom_fields.length > 0 && (
              <CustomFieldsEdit
                fields={product.custom_fields}
                disabled={disabled}
                formPrefix="custom_field_values"
              />
            )}
          </div>
        </div>
      </Card>
    )
  }

  // Edit mode view
  return (
    <Card className={`${className}`}>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--color-charcoal)]">
            Edit Prescription Details
          </h3>
          <div className="flex items-center gap-2">
            {!hasMissingDefaults && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
              >
                <X className="h-4 w-4 mr-1" aria-hidden="true" />
                Cancel
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
            >
              <Check className="h-4 w-4 mr-1" aria-hidden="true" />
              Save changes
            </Button>
          </div>
        </div>

        {/* Edit form */}
        <div className="space-y-5">
          {/* Read-only product info */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 p-4 bg-[var(--color-gray-50)] rounded-md">
            <div>
              <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                Product
              </dt>
              <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                {product.display_name}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                Product Code
              </dt>
              <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                {product.product_code}
              </dd>
            </div>
            {product.ndc && (
              <div>
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  {drugCodeLabel(config?.country)}
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.ndc}
                </dd>
              </div>
            )}
            {product.ingredients && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
                  Ingredients
                </dt>
                <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
                  {product.ingredients}
                </dd>
              </div>
            )}
          </div>

          {/* Read-only strength in edit mode */}
          {product.strength && (
            <div className="p-4 bg-[var(--color-gray-50)] rounded-md">
              <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">Strength</dt>
              <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">{product.strength}</dd>
            </div>
          )}

          {/* Editable: Directions */}
          <FormField
            label="Directions (SIG)"
            required
            htmlFor="dose_specific_directions"
            error={errors.dose_specific_directions?.message}
            helperText={
              modifications.dose_specific_directions ? 'Modified from catalog default' : undefined
            }
          >
            <Textarea
              id="dose_specific_directions"
              rows={3}
              disabled={disabled}
              error={!!errors.dose_specific_directions}
              {...register('dose_specific_directions', {
                required: 'Directions are required',
                maxLength: {
                  value: 2000,
                  message: 'Directions must be 2000 characters or less',
                },
              })}
            />
          </FormField>

          {/* Read-only: Default Additional Directions */}
          {product.default_additional_directions && (
            <div className="p-4 bg-[var(--color-gray-50)] rounded-md">
              <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">Additional Directions (read-only)</dt>
              <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">{product.default_additional_directions}</dd>
            </div>
          )}

          {/* Read-only: Medical Necessity */}
          {product.medical_necessity_statement && (
            <div className="p-4 bg-[var(--color-gray-50)] rounded-md">
              <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">Medical Necessity (read-only)</dt>
              <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">{product.medical_necessity_statement}</dd>
            </div>
          )}

          {/* Editable: Quantity */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <FormField
                label={`Quantity (${product.unit_amount})`}
                required
                htmlFor="quantity"
                error={errors.quantity?.message}
                helperText={
                  getQuantityTotalLabel(product.unit_amount, currentQuantity) ?? undefined
                }
              >
                {isPackUnit ? (
                  <Input
                    id="quantity"
                    type="number"
                    // Native bounds widen to include the catalog default, and
                    // step="any" allows a decimal one, so browser validation never
                    // blocks an untouched default the validate rule below allows
                    min={Math.min(1, Number(product.quantity) || 1)}
                    max={Math.max(MAX_PACK_QUANTITY, Number(product.quantity) || MAX_PACK_QUANTITY)}
                    step="any"
                    disabled={disabled}
                    error={!!errors.quantity}
                    {...register('quantity', {
                      required: 'Quantity is required',
                      // An untouched catalog default always passes
                      validate: (v) =>
                        v === product.quantity ||
                        (/^\d+(\.0+)?$/.test(v) &&
                          Number(v) >= 1 &&
                          Number(v) <= MAX_PACK_QUANTITY) ||
                        `Quantity must be a whole number between 1 and ${MAX_PACK_QUANTITY}`,
                    })}
                  />
                ) : (
                  <Input
                    id="quantity"
                    type="text"
                    inputMode="decimal"
                    disabled={disabled}
                    error={!!errors.quantity}
                    {...register('quantity', {
                      required: 'Quantity is required',
                      pattern: {
                        value: /^\d+(\.\d+)?$/,
                        message: 'Please enter a valid number',
                      },
                    })}
                  />
                )}
              </FormField>
            </div>
            <div>
              <FormField
                label="Unit/Amount"
                htmlFor="unit_amount"
              >
                <Input
                  id="unit_amount"
                  type="text"
                  value={currentUnitAmount}
                  disabled
                  className="bg-[var(--color-gray-50)]"
                />
                <input type="hidden" {...register('unit_amount')} />
              </FormField>
            </div>
          </div>

          {/* Editable: Days Supply and Refills */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              label="Days Supply"
              required
              htmlFor="days_supply"
              error={errors.days_supply?.message}
            >
              <Input
                id="days_supply"
                type="number"
                min={1}
                max={365}
                disabled={disabled}
                error={!!errors.days_supply}
                {...register('days_supply', {
                  required: 'Days supply is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Days supply must be at least 1' },
                  max: {
                    value: 365,
                    message: 'Days supply must be 365 or less',
                  },
                })}
              />
            </FormField>

            <FormField
              label="Refills"
              required
              htmlFor="refills"
              error={errors.refills?.message}
            >
              <Select
                options={REFILL_OPTIONS}
                value={currentRefills != null && currentRefills !== ('' as unknown as number) ? String(currentRefills) : ''}
                onChange={(value) => setValue('refills', value !== '' ? Number(value) : ('' as unknown as number), { shouldValidate: true })}
                placeholder="Select refills"
                disabled={disabled}
                error={!!errors.refills}
              />
              <input
                type="hidden"
                {...register('refills', {
                  validate: (v) => (v != null && v !== ('' as unknown as number)) || 'Refills is required',
                })}
              />
            </FormField>
          </div>

          {/* Reset to defaults button (only when catalog has defaults to reset to) */}
          {hasAnyModification && !hasMissingDefaults && (
            <div className="pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={disabled}
                className="text-[var(--color-gray-600)]"
              >
                <RotateCcw className="h-4 w-4 mr-1" aria-hidden="true" />
                Reset all to catalog defaults
              </Button>
            </div>
          )}

          {/* Product notes read-only (always read-only, even in edit mode) */}
          {product.product_notes && product.product_notes.length > 0 && (
            <ProductNotesReadOnly notes={product.product_notes} />
          )}

          {/* Custom fields edit mode */}
          {product.custom_fields && product.custom_fields.length > 0 && (
            <CustomFieldsEdit
              fields={product.custom_fields}
              disabled={disabled}
              formPrefix="custom_field_values"
            />
          )}
        </div>
      </div>
    </Card>
  )
}

/* --------------------------------------------------------------------------
   Product Notes Sub-Component (CustomFieldsEdit lives in ./CustomFieldsEdit)
   -------------------------------------------------------------------------- */

/**
 * Read-only display of product notes (informational label/value pairs from catalog).
 */
function ProductNotesReadOnly({ notes }: { notes: ProductNoteDefinition[] }) {
  return (
    <div className="border-t border-[var(--color-gray-200)] pt-4 mt-4">
      <h4 className="text-sm font-semibold text-[var(--color-gray-500)] mb-3">
        Product Notes
      </h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {notes.map((note, index) => (
          <div key={index}>
            <dt className="text-sm font-medium text-[var(--color-gray-500)] break-words">
              {note.label}
            </dt>
            <dd className="mt-1 text-base text-[var(--color-charcoal)] break-words">
              {note.value}
            </dd>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PrescriptionDetails
